/**
 * Type definitions for Thesys C1 components and actions.
 */

/**
 * Thesys C1 component structure.
 * Components can be objects or JSON strings.
 */
export type C1Component = C1ComponentObject | string;

/**
 * Thesys C1 component object structure.
 */
export interface C1ComponentObject {
  component: string;
  props?: Record<string, unknown>;
  children?: C1Component[];
  [key: string]: unknown;
}

/**
 * Form field value structure.
 */
export interface FormFieldValue {
  value?: string | number | boolean;
  [key: string]: unknown;
}

/**
 * Taxable drawdown form data structure.
 */
export interface TaxableDrawdownFormData {
  select_taxable_account_for_drawdown?: FormFieldValue;
  taxable_drawdown_metric?: FormFieldValue;
  [key: string]: unknown;
}

/**
 * IRA drawdown form data structure.
 */
export interface IRADrawdownFormData {
  select_ira_account_for_drawdown?: FormFieldValue;
  ira_drawdown_metric?: FormFieldValue;
  [key: string]: unknown;
}

/**
 * C1 action parameters structure.
 */
export interface C1ActionParams {
  llmFriendlyMessage?: string;
  humanFriendlyMessage?: string;
  form_taxable_drawdown_request?: TaxableDrawdownFormData;
  form_ira_drawdown_request?: IRADrawdownFormData;
  [key: string]: unknown;
}

/**
 * C1 action structure from Thesys C1 components.
 */
export interface C1Action {
  params?: C1ActionParams;
  llmFriendlyMessage?: string;
  humanFriendlyMessage?: string;
  [key: string]: unknown;
}

/**
 * Tool call structure from API responses.
 */
export interface ToolCall {
  tool_name: string;
  arguments?: Record<string, unknown>;
  result?: unknown;
  error?: string;
}

/**
 * Tool call structure for ChatMessage.
 */
export interface ChatToolCall {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result?: unknown;
  error?: string;
}

/**
 * Type guard to check if a value is a C1ComponentObject.
 */
export function isC1ComponentObject(value: unknown): value is C1ComponentObject {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'component' in value &&
    typeof (value as C1ComponentObject).component === 'string'
  );
}

/**
 * Type guard to check if a value is a C1Action.
 */
export function isC1Action(value: unknown): value is C1Action {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  );
}

/**
 * Type guard to check if a value is a ToolCall.
 */
export function isToolCall(value: unknown): value is ToolCall {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'tool_name' in value &&
    typeof (value as ToolCall).tool_name === 'string'
  );
}

